/**
 * SKETCHED STAIRS (2026-10-05, Matt: "customize a stair's outline like I can in Revit, so
 * if I want to make a curve … I can enter a sketch mode"; decisions: the WHOLE stair
 * converts to one sketch, "Reset to calculated" is allowed, the risers drawn set the
 * count). Revit's sketched run, generalized to the whole stair:
 *
 *  - EDGES: two connected lines — the stair's two sides — straight or arc pieces, never
 *    joined to each other;
 *  - RISERS: one line per riser, each reaching both edges (trimmed to them);
 *  - WALK LINE: optional — the walk path (the UP arrow); derived through the riser
 *    midpoints when absent.
 *
 * Every area between two consecutive risers is one step: a tread, or — where the risers
 * sit far apart — a landing (a landing IS a deep tread; that is how one rule covers
 * straight, L, U, curved and winder stairs). N risers drawn → N−1 treads/landings, each
 * riser total rise ÷ N.
 *
 * STORAGE: `params.sketch` in the stair's own frame — (s, c) along the first path segment
 * from its start (c to the left) — so Move / Rotate, which move the path, carry the sketch
 * for free. Mirror toggles `sketchMirrored` (MIRROR_SIDE_FLAG), which flips c (and arc
 * bulges) on the way out. The path stays (it IS the frame); its grips hide while sketched.
 */
import type { Path2D, Pt2 } from './types'
import { bulgeToArc } from './arc'

export type StairLineKind = 'edge' | 'riser' | 'walk'
export interface StairSketchSeg { a: Pt2; b: Pt2; bulge?: number }
export interface StairSketch { edges: StairSketchSeg[]; risers: StairSketchSeg[]; walk?: StairSketchSeg[] }
export interface StairSketchIssue { at: Pt2; message: string }

export interface SolvedStairSketch {
  /** riser lines bottom → top, trimmed to the edges (flattened), each running edge0 → edge1 */
  risers: Pt2[][]
  /** the two edges, each oriented bottom → top */
  edges: [Pt2[], Pt2[]]
  /** step k (0 … N−2): the area between riser k and riser k+1 — tops out at (k+1) risers */
  treads: { k: number; poly: Pt2[]; sides: [Pt2[], Pt2[]] }[]
  /** the walk line bottom → top (drawn, or through the riser midpoints) */
  walk: Pt2[]
}

const dist = (p: Pt2, q: Pt2) => Math.hypot(p[0] - q[0], p[1] - q[1])
const TOL = 1e-3

/** a segment as a polyline (arcs flattened) */
export function stairSegPolyline(s: StairSketchSeg, steps = 24): Pt2[] {
  const arc = bulgeToArc(s.a, s.b, s.bulge)
  if (!arc) return [s.a, s.b]
  const n = Math.max(4, Math.ceil((steps * Math.abs(arc.sweep)) / Math.PI))
  const pts: Pt2[] = []
  for (let i = 0; i <= n; i++) {
    const t = arc.a0 + (arc.sweep * i) / n
    pts.push([arc.c[0] + arc.r * Math.cos(t), arc.c[1] + arc.r * Math.sin(t)])
  }
  pts[0] = s.a
  pts[pts.length - 1] = s.b
  return pts
}

/** the stored sketch, or null when the stair is calculated */
export function stairSketchOf(params: Record<string, unknown>): StairSketch | null {
  const sk = params.sketch as StairSketch | null | undefined
  if (!sk || !Array.isArray(sk.edges) || !Array.isArray(sk.risers) || !sk.edges.length || !sk.risers.length) return null
  return sk
}

interface Frame { o: Pt2; u: Pt2; n: Pt2; flip: number }
function frameOf(params: Record<string, unknown>): Frame | null {
  const path = params.path as Path2D | null | undefined
  const nodes = path?.nodes
  if (!nodes || nodes.length < 2) return null
  const o = nodes[0].pt, q = nodes[1].pt
  const L = dist(o, q)
  if (L < 1e-9) return null
  const u: Pt2 = [(q[0] - o[0]) / L, (q[1] - o[1]) / L]
  return { o, u, n: [-u[1], u[0]], flip: params.sketchMirrored ? -1 : 1 }
}

function mapSketch(sk: StairSketch, pt: (p: Pt2) => Pt2, bulgeSign: number): StairSketch {
  const seg = (s: StairSketchSeg): StairSketchSeg => ({ a: pt(s.a), b: pt(s.b), ...(s.bulge ? { bulge: s.bulge * bulgeSign } : {}) })
  return { edges: sk.edges.map(seg), risers: sk.risers.map(seg), ...(sk.walk?.length ? { walk: sk.walk.map(seg) } : {}) }
}

/** the stored (stair-local) sketch in world plan coordinates */
export function stairSketchToWorld(sk: StairSketch, params: Record<string, unknown>): StairSketch | null {
  const f = frameOf(params)
  if (!f) return null
  return mapSketch(sk, (p) => [f.o[0] + f.u[0] * p[0] + f.n[0] * p[1] * f.flip, f.o[1] + f.u[1] * p[0] + f.n[1] * p[1] * f.flip], f.flip)
}

/** a world sketch into the stair's own frame (what `params.sketch` stores) */
export function stairSketchToLocal(sk: StairSketch, params: Record<string, unknown>): StairSketch | null {
  const f = frameOf(params)
  if (!f) return null
  return mapSketch(sk, (p) => {
    const dx = p[0] - f.o[0], dy = p[1] - f.o[1]
    return [dx * f.u[0] + dy * f.u[1], (dx * f.n[0] + dy * f.n[1]) * f.flip]
  }, f.flip)
}

/** the world sketch of a stair element's params, or null */
export function stairWorldSketch(params: Record<string, unknown>): StairSketch | null {
  const sk = stairSketchOf(params)
  return sk ? stairSketchToWorld(sk, params) : null
}

// --- polyline helpers -------------------------------------------------------

function cumLen(pl: Pt2[]): number[] {
  const out = [0]
  for (let i = 1; i < pl.length; i++) out.push(out[i - 1] + dist(pl[i - 1], pl[i]))
  return out
}
function pointAt(pl: Pt2[], cl: number[], t: number): Pt2 {
  if (t <= 0) return pl[0]
  for (let i = 1; i < pl.length; i++) {
    if (cl[i] >= t) {
      const f = (t - cl[i - 1]) / Math.max(1e-12, cl[i] - cl[i - 1])
      return [pl[i - 1][0] + (pl[i][0] - pl[i - 1][0]) * f, pl[i - 1][1] + (pl[i][1] - pl[i - 1][1]) * f]
    }
  }
  return pl[pl.length - 1]
}
/** the piece of a polyline between arc-length stations t0 < t1 */
function subPolyline(pl: Pt2[], cl: number[], t0: number, t1: number): Pt2[] {
  const out: Pt2[] = [pointAt(pl, cl, t0)]
  for (let i = 1; i < pl.length - 1; i++) if (cl[i] > t0 + 1e-9 && cl[i] < t1 - 1e-9) out.push(pl[i])
  out.push(pointAt(pl, cl, t1))
  return out
}
/** every crossing of segment p→q with a polyline: point, station along the polyline,
 *  and the fraction along p→q */
function crossings(p: Pt2, q: Pt2, pl: Pt2[], cl: number[]): { pt: Pt2; t: number; f: number }[] {
  const out: { pt: Pt2; t: number; f: number }[] = []
  const d1x = q[0] - p[0], d1y = q[1] - p[1]
  for (let i = 1; i < pl.length; i++) {
    const r = pl[i - 1], s = pl[i]
    const d2x = s[0] - r[0], d2y = s[1] - r[1]
    const den = d1x * d2y - d1y * d2x
    if (Math.abs(den) < 1e-14) continue
    const f = ((r[0] - p[0]) * d2y - (r[1] - p[1]) * d2x) / den
    const g = ((r[0] - p[0]) * d1y - (r[1] - p[1]) * d1x) / den
    if (f < -1e-9 || f > 1 + 1e-9 || g < -1e-9 || g > 1 + 1e-9) continue
    out.push({ pt: [p[0] + d1x * f, p[1] + d1y * f], t: cl[i - 1] + g * (cl[i] - cl[i - 1]), f })
  }
  return out
}

/** chain loose segments into connected polylines (in drawing order where possible) */
function chainSegs(segs: StairSketchSeg[]): Pt2[][] {
  const pieces = segs.map((s) => stairSegPolyline(s)).filter((p) => p.length >= 2 && dist(p[0], p[p.length - 1]) > 1e-6)
  const chains: Pt2[][] = []
  const used = new Array(pieces.length).fill(false)
  for (let i = 0; i < pieces.length; i++) {
    if (used[i]) continue
    used[i] = true
    let chain = [...pieces[i]]
    let grew = true
    while (grew) {
      grew = false
      for (let j = 0; j < pieces.length; j++) {
        if (used[j]) continue
        const pc = pieces[j]
        const head = chain[0], tail = chain[chain.length - 1]
        if (dist(tail, pc[0]) < TOL) chain = [...chain, ...pc.slice(1)]
        else if (dist(tail, pc[pc.length - 1]) < TOL) chain = [...chain, ...[...pc].reverse().slice(1)]
        else if (dist(head, pc[pc.length - 1]) < TOL) chain = [...pc.slice(0, -1), ...chain]
        else if (dist(head, pc[0]) < TOL) chain = [...[...pc].reverse().slice(0, -1), ...chain]
        else continue
        used[j] = true
        grew = true
      }
    }
    chains.push(chain)
  }
  return chains
}

/**
 * Solve a world sketch into steps. Returns issues (each with a spot to circle) instead
 * when it can't — the editor refuses Finish on them, the geometry falls back to the
 * calculated stair.
 */
export function solveStairSketch(sk: StairSketch): { ok: true; solved: SolvedStairSketch } | { ok: false; issues: StairSketchIssue[] } {
  const issues: StairSketchIssue[] = []
  const chains = chainSegs(sk.edges)
  if (chains.length !== 2) {
    const at = chains[0]?.[0] ?? sk.edges[0]?.a ?? [0, 0]
    return { ok: false, issues: [{ at, message: chains.length < 2 ? 'draw two edges — the stair\'s two sides' : `the edges make ${chains.length} separate lines — draw exactly two connected sides (they must not join each other)` }] }
  }
  for (const c of chains) {
    if (dist(c[0], c[c.length - 1]) < TOL) return { ok: false, issues: [{ at: c[0], message: 'an edge closes on itself — the two sides must stay separate lines' }] }
  }
  // orientation: the walk line's start, else the first drawn edge's own direction
  let [e0, e1] = chains
  const walkPl = sk.walk?.length ? chainSegs(sk.walk)[0] : null
  if (walkPl && walkPl.length >= 2) {
    const ws = walkPl[0]
    if (Math.min(dist(e0[e0.length - 1], ws), dist(e1[e1.length - 1], ws)) < Math.min(dist(e0[0], ws), dist(e1[0], ws))) {
      e0 = [...e0].reverse(); e1 = [...e1].reverse()
    }
  } else {
    const first = sk.edges[0]
    const firstPl = stairSegPolyline(first)
    // does chain e0 or e1 contain the first drawn edge, and in which direction?
    const owner = [e0, e1].find((c) => c.some((p) => dist(p, first.a) < TOL) && c.some((p) => dist(p, first.b) < TOL))
    if (owner) {
      const ia = owner.findIndex((p) => dist(p, firstPl[0]) < TOL)
      const ib = owner.findIndex((p) => dist(p, firstPl[firstPl.length - 1]) < TOL)
      if (ia > ib) { if (owner === e0) e0 = [...e0].reverse(); else e1 = [...e1].reverse() }
    }
    const lead = owner === e1 ? e1 : e0
    const other = lead === e0 ? e1 : e0
    if (dist(other[0], lead[0]) + dist(other[other.length - 1], lead[lead.length - 1]) > dist(other[0], lead[lead.length - 1]) + dist(other[other.length - 1], lead[0])) {
      if (other === e0) e0 = [...e0].reverse(); else e1 = [...e1].reverse()
    }
  }
  const c0 = cumLen(e0), c1 = cumLen(e1)
  // each riser: where it meets each edge (its own line extended a little past its ends)
  const risers: { pts: Pt2[]; t0: number; t1: number }[] = []
  for (const rs of sk.risers) {
    const pl = stairSegPolyline(rs)
    if (pl.length < 2) continue
    const ext = (a: Pt2, b: Pt2, by: number): Pt2 => { const L = dist(a, b) || 1; return [a[0] + ((a[0] - b[0]) / L) * by, a[1] + ((a[1] - b[1]) / L) * by] }
    const run: Pt2[] = [ext(pl[0], pl[1], 0.5), ...pl.slice(1, -1), ext(pl[pl.length - 1], pl[pl.length - 2], 0.5)]
    const hit = (edge: Pt2[], cl: number[]) => {
      let best: { pt: Pt2; t: number; at: number; off: number } | null = null
      const rc = cumLen(run)
      // the drawn ends sit 6" in from the extended run's ends
      const endA = 0.5, endB = rc[rc.length - 1] - 0.5
      for (let i = 1; i < run.length; i++) {
        for (const x of crossings(run[i - 1], run[i], edge, cl)) {
          const at = rc[i - 1] + x.f * (rc[i] - rc[i - 1])
          // the crossing nearest one of the riser's own drawn ends wins (not the one
          // farthest out: across a well narrower than the 6" reach, that is the
          // OTHER flight's side, which is what refused Finish on narrow U-stairs)
          const off = Math.min(Math.abs(at - endA), Math.abs(at - endB))
          if (!best || off < best.off - 1e-9) best = { pt: x.pt, t: x.t, at, off }
        }
      }
      return best
    }
    const h0 = hit(e0, c0), h1 = hit(e1, c1)
    if (!h0 || !h1) { issues.push({ at: pl[Math.floor(pl.length / 2)], message: 'a riser doesn\'t reach both edges' }); continue }
    // the riser trimmed between its two edge hits, running edge 0 → edge 1
    const rc = cumLen(run)
    const lo = Math.min(h0.at, h1.at), hi = Math.max(h0.at, h1.at)
    let trimmed = subPolyline(run, rc, lo, hi)
    if (h0.at > h1.at) trimmed = trimmed.reverse()
    trimmed[0] = h0.pt; trimmed[trimmed.length - 1] = h1.pt
    risers.push({ pts: trimmed, t0: h0.t, t1: h1.t })
  }
  if (issues.length) return { ok: false, issues }
  if (risers.length < 2) return { ok: false, issues: [{ at: e0[0], message: 'draw at least two risers' }] }
  risers.sort((a, b) => a.t0 - b.t0)
  for (let i = 1; i < risers.length; i++) {
    if (risers[i].t1 <= risers[i - 1].t1 + 1e-6) { issues.push({ at: risers[i].pts[0], message: 'two risers cross each other' }); break }
    if (risers[i].t0 - risers[i - 1].t0 < 1e-3) { issues.push({ at: risers[i].pts[0], message: 'two risers sit on top of each other' }); break }
  }
  if (issues.length) return { ok: false, issues }
  const treads: SolvedStairSketch['treads'] = []
  for (let k = 0; k + 1 < risers.length; k++) {
    const a = risers[k], b = risers[k + 1]
    const side0 = subPolyline(e0, c0, a.t0, b.t0)
    const side1 = subPolyline(e1, c1, a.t1, b.t1).reverse()
    const poly: Pt2[] = [...side0, ...b.pts.slice(1, -1), ...side1, ...[...a.pts].reverse().slice(1, -1)]
    // drop repeats
    const clean = poly.filter((p, i) => dist(p, poly[(i + 1) % poly.length]) > 1e-6)
    treads.push({ k, poly: clean, sides: [side0, subPolyline(e1, c1, a.t1, b.t1)] })
  }
  const mid = (pl: Pt2[]) => { const cl = cumLen(pl); return pointAt(pl, cl, cl[cl.length - 1] / 2) }
  const walk = walkPl && walkPl.length >= 2 ? walkPl : risers.map((r) => mid(r.pts))
  return { ok: true, solved: { risers: risers.map((r) => r.pts), edges: [e0, e1], treads, walk } }
}

/** a stair element's solved sketch (world), or null when calculated / unsolvable */
export function stairSketchSolved(params: Record<string, unknown>): SolvedStairSketch | null {
  const w = stairWorldSketch(params)
  if (!w) return null
  const r = solveStairSketch(w)
  return r.ok ? r.solved : null
}
